import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useHelp } from "@/context/HelpContext";
import { helpForRoles, type HelpArticle } from "@/lib/help";
import { openSupportQuestion, supportScreen } from "@/lib/supportContext";
import { Button } from "./ui/button";
import "./Help.css";

export function HelpSections({ article }: { article: HelpArticle }) {
  return (
    <div className="help-sections">
      {article.sections.map((section) => (
        <section className="help-card" key={section.title}>
          <h3>{section.title}</h3>
          <ol className="help-steps">
            {section.steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

export function HelpPanel({
  article,
  onClose,
}: {
  article: HelpArticle;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const { user } = useAuth();
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const element = dialog.current;
    element?.showModal();
    return () => {
      element?.close();
      previous?.focus();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      className="help-dialog"
      aria-label={`${article.title}: kako da…`}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === dialog.current) onClose();
      }}
    >
      <div className="help-panel">
        <div className="help-grab" aria-hidden="true" />
        <header className="help-panel-heading">
          <h2>{article.title}: kako da…</h2>
          <Button
            variant="tertiary"
            aria-label="Zatvori upute"
            onClick={onClose}
          >
            ×
          </Button>
        </header>
        <p className="help-muted">
          Za ulogu: {user?.roles.map((role) => role.name_hr).join(", ")}
        </p>
        <HelpSections article={article} />
        <footer className="help-actions">
          <Link
            className="help-link-button"
            to="/pomoc/upute"
            onClick={onClose}
          >
            Sve upute
          </Link>
          <Button
            variant="secondary"
            onClick={() => {
              onClose();
              openSupportQuestion();
            }}
          >
            💬 I dalje ne znam
          </Button>
          <Button variant="tertiary" onClick={onClose}>
            Zatvori
          </Button>
        </footer>
      </div>
    </dialog>
  );
}

export function HelpButton() {
  const { articles } = useHelp();
  return articles.length ? <ActiveHelpButton /> : null;
}

function ActiveHelpButton() {
  const { user } = useAuth();
  const { articles } = useHelp();
  const { pathname } = useLocation();
  const [openPath, setOpenPath] = useState<string | null>(null);
  const screen = supportScreen(pathname)?.key;
  const article = helpForRoles(
    articles,
    user?.roles.map((role) => role.code) ?? [],
  ).find((item) => item.screen === screen && screen !== "pomoc");
  if (!article) return null;
  return (
    <span className="help-heading-control" key={pathname}>
      <button
        type="button"
        className="help-question"
        aria-label="Pomoć za ovaj ekran"
        aria-haspopup="dialog"
        onClick={(event) => {
          event.currentTarget.focus();
          setOpenPath(pathname);
        }}
      >
        ?
      </button>
      {openPath === pathname && (
        <HelpPanel
          key={pathname}
          article={article}
          onClose={() => setOpenPath(null)}
        />
      )}
    </span>
  );
}
